/**
 * "My bike" store + header selector.
 *
 * Storage contract (shared with product fit badge, home bike finder and cart recommendations):
 *   localStorage['voge:my-bike'] = JSON { id, handle, title, url }
 *   - id: collection ID (stable across languages; compare with product collection IDs)
 *   - title/url are refreshed from the current page's model list (#my-bike-models) so they
 *     follow the active language/market.
 * Event: document dispatches `mybike:change` with detail { bike } whenever the bike changes
 * (also across tabs via the storage event).
 *
 * Usage from other modules:
 *   import { getBike, setBike, getModels, MY_BIKE_EVENT } from '@theme/my-bike';
 */

const STORAGE_KEY = 'voge:my-bike';
export const MY_BIKE_EVENT = 'mybike:change';

/** @typedef {{ id: string, handle: string, title: string, url: string }} BikeModel */

/** @type {BikeModel[] | null} */
let modelsCache = null;

/** @returns {BikeModel[]} */
export function getModels() {
  if (modelsCache) return modelsCache;
  const source = document.getElementById('my-bike-models');
  try {
    modelsCache = source ? JSON.parse(source.textContent || '[]') : [];
  } catch {
    modelsCache = [];
  }
  return /** @type {BikeModel[]} */ (modelsCache);
}

/** @returns {BikeModel | null} */
export function getBike() {
  /** @type {any} */
  let stored = null;
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    stored = null;
  }
  if (!stored || !stored.id) return null;
  const current = getModels().find((model) => String(model.id) === String(stored.id));
  return current ? { ...stored, ...current } : stored;
}

/** @param {BikeModel | null} model */
export function setBike(model) {
  try {
    if (model) {
      const { id, handle, title, url } = model;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ id, handle, title, url }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* storage unavailable (private mode) – selection lasts for this page only */
  }
  notify();
}

function notify() {
  document.dispatchEvent(new CustomEvent(MY_BIKE_EVENT, { detail: { bike: getBike() } }));
}

/**
 * Model collection IDs among the given collection IDs (only collections of the header model list count).
 * @param {Array<string | number>} collectionIds
 * @returns {string[]}
 */
export function modelIdsOf(collectionIds) {
  const models = new Set(getModels().map((model) => String(model.id)));
  return collectionIds.map(String).filter((id) => models.has(id));
}

/**
 * Model collection(s) recommendations should fit, for a product with the given collections:
 * the rider's bike when the product fits it (or the product has no model, e.g. universal parts),
 * otherwise the product's own model collections. Empty = no model filter.
 * @param {Array<string | number>} productCollectionIds
 * @returns {string[]}
 */
export function targetModelIds(productCollectionIds) {
  const bike = getBike();
  const own = modelIdsOf(productCollectionIds);
  if (bike && (own.length === 0 || own.includes(String(bike.id)))) return [String(bike.id)];
  return own;
}

/**
 * Recommendation rule (complete-your-setup, cart recommendations):
 * 1. parts in a target model collection (the current product's model or the rider's bike),
 * 2. then universal parts (no model collection, or data-universal="true") as filler,
 * never parts listed only for other models. Without targets only universal parts remain.
 * Apparel / gifts (data-gift="true") are dropped unless allowGifts (the current product is apparel).
 * Elements carry data-collection-ids="1,2,3", data-universal and data-gift.
 * @template {HTMLElement} T
 * @param {T[]} elements
 * @param {string[]} targetIds
 * @param {boolean} [allowGifts]
 * @returns {T[]}
 */
export function rankByModel(elements, targetIds, allowGifts = false) {
  const models = new Set(getModels().map((model) => String(model.id)));
  /** @type {T[]} */
  const fits = [];
  /** @type {T[]} */
  const universal = [];
  for (const element of elements) {
    if (!allowGifts && element.dataset.gift === 'true') continue;
    const own = (element.dataset.collectionIds || '').split(',').filter((id) => models.has(id));
    if (targetIds.length && own.some((id) => targetIds.includes(id))) fits.push(element);
    else if (own.length === 0 || element.dataset.universal === 'true') universal.push(element);
  }
  return [...fits, ...universal];
}

/**
 * @param {string} id - Model collection ID.
 * @returns {BikeModel | undefined}
 */
export function getModel(id) {
  return getModels().find((model) => String(model.id) === String(id));
}

window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) notify();
});

/**
 * Header "My bike" button + popover panel.
 * Markup: snippets/my-bike-selector.liquid
 */
class MyBikeSelector extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.toggle = /** @type {HTMLButtonElement} */ (this.querySelector('[data-my-bike-toggle]'));
    this.panel = /** @type {HTMLElement} */ (this.querySelector('[data-my-bike-panel]'));
    if (!this.toggle || !this.panel) return;

    this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()), { signal });
    this.panel.addEventListener('click', this.#onPanelClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('keyup', this.#onKeyup, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener(MY_BIKE_EVENT, () => this.render(), { signal });

    this.render();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get isOpen() {
    return this.toggle?.getAttribute('aria-expanded') === 'true';
  }

  open() {
    this.render();
    this.panel.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    const inline = this.hasAttribute('data-inline');
    if (inline) this.showChooser(true);
    const focusTarget = /** @type {HTMLElement | null} */ (
      this.panel.querySelector(
        getBike() && !inline ? '[data-my-bike-link]' : '[data-my-bike-option][aria-pressed="true"], [data-my-bike-option]'
      )
    );
    focusTarget?.focus();
  }

  /** @param {boolean} [returnFocus] */
  close(returnFocus = false) {
    if (!this.panel || this.panel.hidden) return;
    this.panel.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.showChooser(false);
    if (returnFocus) this.toggle.focus();
  }

  /** @param {boolean} show */
  showChooser(show) {
    const current = this.querySelector('[data-my-bike-current]');
    const chooser = this.querySelector('[data-my-bike-chooser]');
    const hasBike = Boolean(getBike());
    if (current instanceof HTMLElement) current.hidden = show || !hasBike;
    if (chooser instanceof HTMLElement) chooser.hidden = !show && hasBike;
  }

  render() {
    const bike = getBike();
    const value = this.querySelector('[data-my-bike-value]');
    if (value) value.textContent = bike ? bike.title : this.dataset.emptyValue || this.dataset.emptyLabel || '';
    this.classList.toggle('my-bike--set', Boolean(bike));

    for (const link of /** @type {NodeListOf<HTMLAnchorElement>} */ (this.querySelectorAll('[data-my-bike-link]'))) {
      if (bike) {
        link.href = bike.url;
        link.textContent = (this.dataset.shopLabel || '[model]').replace('[model]', bike.title);
      }
      // Card variant (menu drawer): the shop link only exists once a bike is chosen
      if (link.hasAttribute('data-hide-empty')) link.hidden = !bike;
    }
    const action = this.querySelector('[data-my-bike-action]');
    if (action) action.textContent = (bike ? this.dataset.changeLabel : this.dataset.emptyLabel) || '';
    const name = this.querySelector('[data-my-bike-name]');
    if (name && bike) name.textContent = bike.title;

    for (const option of this.querySelectorAll('[data-my-bike-option]')) {
      option.setAttribute('aria-pressed', String(Boolean(bike) && option.getAttribute('data-id') === String(bike.id)));
    }
    this.showChooser(this.hasAttribute('data-inline'));
  }

  /** @param {MouseEvent} event */
  #onPanelClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const option = target.closest('[data-my-bike-option]');
    if (option instanceof HTMLElement) {
      const { id = '', handle = '', title = '', url = '' } = option.dataset;
      setBike({ id, handle, title, url });
      if (this.hasAttribute('data-inline')) {
        // Card variant: the chooser is an inline list; close it and return to the card
        this.close(false);
        /** @type {HTMLElement | null} */ (this.querySelector('[data-my-bike-link]:not([hidden])') || this.toggle)?.focus();
        return;
      }
      this.showChooser(false);
      /** @type {HTMLElement | null} */ (this.querySelector('[data-my-bike-link]'))?.focus();
      return;
    }
    if (target.closest('[data-my-bike-change]')) {
      this.showChooser(true);
      /** @type {HTMLElement | null} */ (this.querySelector('[data-my-bike-option]'))?.focus();
      return;
    }
    if (target.closest('[data-my-bike-clear]')) {
      setBike(null);
      this.showChooser(true);
      /** @type {HTMLElement | null} */ (this.querySelector('[data-my-bike-option]'))?.focus();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.stopPropagation();
      this.#closedByEscape = true;
      this.close(true);
    }
  };

  /**
   * The menu drawer closes on Escape keyup; swallow the keyup that belongs to closing this panel.
   * @param {KeyboardEvent} event
   */
  #onKeyup = (event) => {
    if (event.key === 'Escape' && this.#closedByEscape) {
      event.stopPropagation();
      this.#closedByEscape = false;
    }
  };

  #closedByEscape = false;

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (this.hasAttribute('data-inline')) return;
    if (this.isOpen && event.target instanceof Node && !this.contains(event.target)) this.close();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (this.hasAttribute('data-inline')) return;
    if (this.isOpen && next instanceof Node && !this.contains(next)) this.close();
  };
}

if (!customElements.get('my-bike-selector')) {
  customElements.define('my-bike-selector', MyBikeSelector);
}
